import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Masonry from 'react-masonry-css';
import {
    IconPlus, IconTrash, IconEdit, IconCopy, IconPrinter,
    IconAdjustmentsHorizontal, IconRefresh, IconSearch, IconX,
    IconArrowsSort, IconClock, IconSortAscendingLetters, IconChevronDown, IconCalendar,
    IconLayoutGrid, IconList, IconLoader2, IconPencil,
    IconUser, IconGripVertical, IconTrendingUp,
} from '@tabler/icons-react';
import {
    DndContext, DragOverlay, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable,
} from '@dnd-kit/sortable';
import { notifications } from '@/lib/notifications';
import { useDebouncedValue } from '@/lib/hooks';
import { Sheet, SheetContent, SheetTitle } from '../../../components/ui/sheet';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuLabel, DropdownMenuSeparator,
    DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from '../../../components/ui/dropdown-menu';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import printTemplateService from '../../../services/printTemplateService';
import printTemplateCategoryService from '../../../services/printTemplateCategoryService';

const PAGE_SIZE = 18;
const VIEW_KEY = 'adminPrintTemplatesView';
const masonryBreakpoints = { default: 5, 1920: 5, 1440: 4, 1280: 3, 1024: 2, 640: 1 };
const CARD_FOOTER_H = 0.32; // footer + margin, relative to column width

const STATUSES = ['active', 'draft', 'archived'];
const STATUS_PILL = {
    active:   'bg-emerald-500/90 text-white',
    draft:    'bg-amber-500/90 text-white',
    archived: 'bg-zinc-600/85 text-white',
};

// Date-range filter → epoch-ms cutoff. Templates with createdAt >= cutoff pass.
// 'today' = since local midnight; the rest are rolling windows from now.
function dateRangeCutoff(range) {
    const now = new Date();
    if (range === 'today') {
        const d = new Date(now);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
    }
    const days = { '3days': 3, '7days': 7, '30days': 30 }[range];
    if (!days) return null;
    return now.getTime() - days * 24 * 60 * 60 * 1000;
}

/** "1.2k" rather than "1247" — the badge is a glance, not a figure. */
function formatUses(n) {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
}

/** Card height estimate for the masonry (height/width), from the page size. */
function aspectOf(tpl) {
    const w = Number(tpl.width) || 1050;
    const h = Number(tpl.height) || 600;
    return h / w;
}

/**
 * Greedy balanced masonry — each card goes to the currently-shortest column so
 * columns stay even, since a fixed-row grid either crops these thumbnails
 * (wide banners to tall posters) or leaves holes under the short ones.
 *
 * `sequential` switches that off for round-robin placement. Balanced packing
 * puts cards wherever they fit, so the reading order stops matching the list
 * order — fine while browsing, wrong when the order IS the content (a
 * hand-arranged shelf, or one being dragged into shape).
 */
function BalancedMasonry({ items, columnCount, renderItem, heightOf, sequential = false }) {
    const columns = useMemo(() => {
        const cols = Array.from({ length: columnCount }, () => []);
        if (sequential) {
            items.forEach((it, i) => cols[i % columnCount].push(it));
            return cols;
        }
        const heights = new Array(columnCount).fill(0);
        for (const it of items) {
            const est = heightOf(it);
            let min = 0;
            for (let i = 1; i < columnCount; i++) if (heights[i] < heights[min]) min = i;
            cols[min].push(it);
            heights[min] += est;
        }
        return cols;
    }, [items, columnCount, heightOf, sequential]);

    return (
        <div className="flex w-auto -ml-4">
            {columns.map((col, i) => (
                <div key={i} className="pl-4 bg-clip-padding flex-1 min-w-0">
                    {col.map(renderItem)}
                </div>
            ))}
        </div>
    );
}

/**
 * Cards here are all different heights, and the built-in sorting strategies
 * shift the other cards assuming a grid of equal boxes — which lands them in
 * visibly wrong places. Nothing shifts instead: the dragged card rides a
 * DragOverlay and the card it would land on is ringed.
 */
const noShiftStrategy = () => null;

// ---------------------------------------------------------------------------
// Admin print-templates gallery — filter sidebar, masonry grid, and
// drag-to-reorder (print templates are an ordered shelf, not a feed)
// ---------------------------------------------------------------------------
export default function AdminPrintTemplatesPage() {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === 'bn';
    const fmt = (n) => (n ?? 0).toLocaleString(isBn ? 'bn-BD' : 'en-US');

    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const loadTokenRef = useRef(0); // a newer search supersedes a slower, older load

    // ── Column count from the ACTUAL grid width (viewport minus the sidebar),
    //    targeting ~250px per card, so wider screens pack more per row. ──
    const gridRef = useRef(null);
    const [gridWidth, setGridWidth] = useState(0);
    useEffect(() => {
        const el = gridRef.current;
        if (!el) return;
        const measure = () => setGridWidth(el.clientWidth);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const columnCount = gridWidth ? Math.max(1, Math.min(6, Math.floor(gridWidth / 250))) : 3;

    // Measured thumbnail ratios (height/width) by id, so the masonry balances
    // from real heights once the images land rather than the page-size estimate.
    const [ratios, setRatios] = useState({});
    const reportRatio = useCallback((id, r) => {
        if (!r || !isFinite(r)) return;
        setRatios((prev) => (prev[id] ? prev : { ...prev, [id]: r }));
    }, []);
    const heightOf = useCallback(
        (it) => (ratios[it.id] ?? aspectOf(it)) + CARD_FOOTER_H,
        [ratios],
    );

    // ── Filter state ──
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchQuery, 300);
    const [activeCategory, setActiveCategory] = useState('all'); // 'all' | 'uncategorized' | name
    // ?status=draft deep-links to a pre-filtered list.
    const [searchParams] = useSearchParams();
    const [statusFilter, setStatusFilter] = useState(() =>
        STATUSES.includes(searchParams.get('status')) ? searchParams.get('status') : 'all');
    const [pricing, setPricing] = useState('all');
    const [sort, setSort] = useState('order');
    const [dateRange, setDateRange] = useState('all');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);
    const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || 'grid');
    const changeView = (v) => { setView(v); localStorage.setItem(VIEW_KEY, v); };

    // ── Action state ──
    const [deleteTarget, setDeleteTarget] = useState(null);
    // Duplicating creates a row and navigates away from whatever the admin was
    // looking at, so it is confirmed rather than fired straight off a hover icon.
    const [duplicateTarget, setDuplicateTarget] = useState(null);
    const [duplicatingId, setDuplicatingId] = useState(null);
    const [togglingId, setTogglingId] = useState(null);

    // ── Reorder state ──
    const [savingOrder, setSavingOrder] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [overId, setOverId] = useState(null);
    // A drag that ends over a card still fires a click on release, which would
    // open the editor for whatever the pointer landed on. Swallow clicks for a
    // moment after any drag.
    const dragEndedAt = useRef(0);

    const sensors = useSensors(
        // 8px of travel before a drag starts, so the card's own link and its
        // hover action buttons keep working.
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    // ── Fetch (admin sees ALL statuses; server search, filters client-side) ──
    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const params = { status: 'all' };
            if (debouncedSearch) params.search = debouncedSearch.trim();
            const token = ++loadTokenRef.current;
            // The grid renders the first page at once; the rest streams in behind it.
            const res = await printTemplateService.listAll(params, (data) => {
                if (token !== loadTokenRef.current) return;
                setTemplates(data);
                setLoading(false);
            });
            if (token === loadTokenRef.current) setTemplates(res?.data || []);
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err.message || t('printTemplatesAdmin.failedToLoad'),
                color: 'red',
            });
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
    useEffect(() => {
        printTemplateCategoryService.list()
            .then(r => setCategories(r?.data || []))
            .catch(() => {});
    }, []);

    // ── Derived ──
    const activeCategoryObj = activeCategory === 'all' || activeCategory === 'uncategorized'
        ? null
        : categories.find(c => c.slug === activeCategory) || null;
    const activeCategoryId = activeCategoryObj?.id ?? null;
    const getCatName = (cat) => (isBn && cat?.nameBn) ? cat.nameBn : cat?.name;

    const countByCategory = useMemo(() => {
        const map = new Map();
        let uncategorized = 0;
        templates.forEach((tpl) => {
            if (tpl.categoryId == null) { uncategorized += 1; return; }
            map.set(tpl.categoryId, (map.get(tpl.categoryId) || 0) + 1);
        });
        return { map, uncategorized };
    }, [templates]);

    const countByStatus = useMemo(() => {
        const map = { active: 0, draft: 0, archived: 0 };
        templates.forEach((tpl) => { if (map[tpl.status] != null) map[tpl.status] += 1; });
        return map;
    }, [templates]);

    // Category marquee: keep the SPEED constant (px/sec) regardless of how many
    // categories there are. The row is duplicated for a seamless loop, so it
    // travels one set-width per cycle; duration = that width ÷ speed. Without
    // this a fixed duration races on a long list and crawls on a short one.
    const MARQUEE_SPEED = 22;
    const marqueeRowRef = useRef(null);
    const [marqueeDuration, setMarqueeDuration] = useState(60);
    const [marqueePaused, setMarqueePaused] = useState(false);
    useEffect(() => {
        const el = marqueeRowRef.current;
        if (!el || categories.length === 0) return;
        const measure = () => {
            const oneSetWidth = el.scrollWidth / 2;
            if (oneSetWidth > 0) setMarqueeDuration(oneSetWidth / MARQUEE_SPEED);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [categories]);

    // ── Actions ──
    const handleStatusToggle = async (tpl) => {
        const nextStatus = tpl.status === 'active' ? 'draft' : 'active';
        setTogglingId(tpl.id);
        try {
            await printTemplateService.update(tpl.id, { status: nextStatus });
            setTemplates(prev => prev.map(x => x.id === tpl.id ? { ...x, status: nextStatus } : x));
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err.message, color: 'red' });
        } finally {
            setTogglingId(null);
        }
    };

    const handleDuplicate = async () => {
        const tpl = duplicateTarget;
        if (!tpl) return;
        const id = tpl.id;
        setDuplicatingId(id);
        try {
            const res = await printTemplateService.duplicate(id);
            notifications.show({ title: t('common.success'), message: t('printTemplatesAdmin.duplicated'), color: 'green' });
            if (res?.data?.id) navigate(`/admin/print-templates/${res.data.id}/edit`);
            else fetchTemplates();
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err.message, color: 'red' });
        } finally {
            setDuplicatingId(null);
            setDuplicateTarget(null);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await printTemplateService.remove(deleteTarget.id);
            notifications.show({ title: t('common.success'), message: t('printTemplatesAdmin.deleted'), color: 'green' });
            setDeleteTarget(null);
            fetchTemplates();
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err.message, color: 'red' });
        }
    };

    // ── Apply filters + sort ──
    const filtered = useMemo(() => {
        let list = templates;
        if (activeCategory === 'uncategorized') list = list.filter(tpl => tpl.categoryId == null);
        else if (activeCategoryId != null) list = list.filter(tpl => tpl.categoryId === activeCategoryId);
        if (statusFilter !== 'all') list = list.filter(tpl => tpl.status === statusFilter);
        if (pricing !== 'all') list = list.filter(tpl => (pricing === 'free' ? tpl.isFree === true : tpl.isFree !== true));
        if (dateRange !== 'all') {
            const cutoff = dateRangeCutoff(dateRange);
            if (cutoff != null) list = list.filter(tpl => new Date(tpl.createdAt || 0).getTime() >= cutoff);
        }

        // 'order' is the admin's own arrangement, which is the order the server
        // already served — leave it exactly as it came back.
        if (sort === 'order') return list;
        const sorted = [...list];
        if (sort === 'popular') sorted.sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0) || b.id - a.id);
        else if (sort === 'newest') sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0) || b.id - a.id);
        else if (sort === 'oldest') sorted.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0) || a.id - b.id);
        else if (sort === 'name') sorted.sort((a, b) => String((isBn && a.titleBn) || a.title || '').localeCompare(String((isBn && b.titleBn) || b.title || ''), i18n.language));
        return sorted;
    }, [templates, activeCategory, activeCategoryId, statusFilter, pricing, sort, dateRange, isBn, i18n.language]);

    useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeCategory, statusFilter, pricing, sort, dateRange, debouncedSearch]);

    const shown = filtered.slice(0, visibleCount);
    const hasMore = filtered.length > visibleCount;

    // Infinite scroll: reveal the next page when the sentinel scrolls into view.
    const sentinelRef = useRef(null);
    useEffect(() => {
        if (!hasMore) return;
        const node = sentinelRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            (entries) => { if (entries[0]?.isIntersecting) setVisibleCount(c => c + PAGE_SIZE); },
            { rootMargin: '400px' },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, shown.length]);

    // Dragging only means something while the grid IS the saved order. Under any
    // other sort the position a card is dropped into is the sort's, not the
    // admin's, so there is nothing coherent to write back.
    const reorderable = sort === 'order';

    /**
     * `next` is the visible cards in their new order. The templates hidden by
     * the current filters (or below the paging cutoff) are left exactly where
     * they are — server-side, only the slots the visible ones already held get
     * rewritten — so an order made inside one category still means something
     * under "All".
     */
    const applyOrder = useCallback(async (next) => {
        const previous = templates;
        const movingIds = new Set(next.map((tpl) => tpl.id));
        let cursor = 0;
        setTemplates(templates.map((tpl) => (movingIds.has(tpl.id) ? next[cursor++] : tpl)));

        setSavingOrder(true);
        try {
            await printTemplateService.reorder(next.map((tpl) => tpl.id));
        } catch (err) {
            setTemplates(previous);
            notifications.show({
                title: t('common.error'),
                message: err.message || t('printTemplatesAdmin.orderSaveFailed'),
                color: 'red',
            });
        } finally {
            setSavingOrder(false);
        }
    }, [templates, t]);

    const handleDragEnd = useCallback((event) => {
        dragEndedAt.current = Date.now();
        setActiveId(null);
        setOverId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = shown.findIndex((tpl) => tpl.id === active.id);
        const newIndex = shown.findIndex((tpl) => tpl.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        applyOrder(arrayMove(shown, oldIndex, newIndex));
    }, [shown, applyOrder]);

    const activeTemplate = useMemo(
        () => (activeId == null ? null : shown.find((tpl) => tpl.id === activeId) || null),
        [activeId, shown],
    );

    const filtersDirty = activeCategory !== 'all' || statusFilter !== 'all' || pricing !== 'all'
        || sort !== 'order' || dateRange !== 'all' || !!searchQuery;

    const resetFilters = () => {
        setActiveCategory('all');
        setStatusFilter('all');
        setPricing('all');
        setSort('order');
        setDateRange('all');
        setSearchQuery('');
    };

    // Filter controls shared between the desktop sidebar and mobile drawer.
    const filterHeaderRow = (
        <div className="flex items-center gap-2 mb-4">
            <IconAdjustmentsHorizontal size={17} className="text-[#E2702E]" />
            <span className="text-sm font-bold text-[#1C1B19] dark:text-zinc-100">{t('worldCupJersey.filters')}</span>
            <button
                onClick={resetFilters}
                disabled={!filtersDirty}
                className="ml-auto inline-flex items-center gap-1 text-xs font-semibold transition-colors text-[#E2702E] hover:text-[#E2702E]/80 disabled:text-[#C3BEB4] dark:disabled:text-zinc-600 disabled:cursor-not-allowed"
            >
                <IconRefresh size={13} /> {t('worldCupJersey.reset')}
            </button>
        </div>
    );

    const filterBody = (
        <>
            {/* Category */}
            <FilterLabel>{t('worldCupJersey.category')}</FilterLabel>
            <div className={`flex flex-col gap-0.5 mb-5 ${categories.length + 1 > 10 ? 'max-h-[360px] overflow-y-auto pr-1 [scrollbar-width:thin]' : ''}`}>
                {loading ? (
                    <CategoryRowsSkeleton />
                ) : (
                    <>
                        <CategoryRow active={activeCategory === 'all'} label={t('worldCupJersey.all')} count={fmt(templates.length)} onClick={() => setActiveCategory('all')} />
                        {categories.map((c) => (
                            <CategoryRow
                                key={c.slug}
                                active={activeCategory === c.slug}
                                label={getCatName(c)}
                                count={fmt(countByCategory.map.get(c.id) || 0)}
                                onClick={() => setActiveCategory(c.slug)}
                            />
                        ))}
                        {countByCategory.uncategorized > 0 && (
                            <CategoryRow
                                active={activeCategory === 'uncategorized'}
                                label={t('printTemplatesAdmin.uncategorized')}
                                count={fmt(countByCategory.uncategorized)}
                                onClick={() => setActiveCategory('uncategorized')}
                            />
                        )}
                    </>
                )}
            </div>

            {/* Status (admin-only) */}
            <FilterLabel>{t('printTemplatesAdmin.status')}</FilterLabel>
            <div className="flex flex-col gap-0.5 mb-5">
                <CategoryRow active={statusFilter === 'all'} label={t('worldCupJersey.all')} count={fmt(templates.length)} onClick={() => setStatusFilter('all')} />
                {STATUSES.map(s => (
                    <CategoryRow key={s} active={statusFilter === s} label={t(`printTemplatesAdmin.status_${s}`)} count={fmt(countByStatus[s] || 0)} onClick={() => setStatusFilter(s)} />
                ))}
            </div>

            {/* Pricing */}
            <FilterLabel>{t('printMedia.pricingLabel')}</FilterLabel>
            <div className="flex flex-col gap-0.5 mb-5">
                <RadioRow active={pricing === 'all'} title={t('printMedia.pricingAll')} onClick={() => setPricing('all')} />
                <RadioRow active={pricing === 'free'} title={t('printMedia.pricingFree')} hint={t('printMedia.pricingFreeHint')} onClick={() => setPricing('free')} />
                <RadioRow active={pricing === 'paid'} title={t('printMedia.pricingPaid')} hint={t('printMedia.pricingPaidHint')} onClick={() => setPricing('paid')} />
            </div>

            {/* Sort */}
            <FilterLabel>{t('worldCupJersey.sort')}</FilterLabel>
            <div className="flex flex-col gap-0.5">
                <SortRow active={sort === 'order'} icon={IconArrowsSort} label={t('printTemplatesAdmin.sortManual')} onClick={() => setSort('order')} />
                <SortRow active={sort === 'popular'} icon={IconTrendingUp} label={t('worldCupJersey.sortPopular')} onClick={() => setSort('popular')} />
                <SortRow active={sort === 'newest'} icon={IconClock} label={t('worldCupJersey.sortNewest')} onClick={() => setSort('newest')} />
                <SortRow active={sort === 'oldest'} icon={IconClock} label={t('worldCupJersey.sortOldest')} onClick={() => setSort('oldest')} />
                <SortRow active={sort === 'name'} icon={IconSortAscendingLetters} label={t('worldCupJersey.sortName')} onClick={() => setSort('name')} />
            </div>
        </>
    );

    const headerTitle = activeCategory === 'uncategorized'
        ? t('printTemplatesAdmin.uncategorized')
        : getCatName(activeCategoryObj) || t('navigation.printTemplates');

    const grid = view === 'grid' ? (
        <BalancedMasonry
            items={shown}
            columnCount={columnCount}
            heightOf={heightOf}
            sequential={reorderable}
            renderItem={(tpl) => (
                <SortablePrintCard
                    key={tpl.id}
                    tpl={tpl}
                    isBn={isBn}
                    reorderable={reorderable}
                    isOver={overId === tpl.id && activeId !== tpl.id}
                    dragEndedAt={dragEndedAt}
                    togglingId={togglingId}
                    duplicatingId={duplicatingId}
                    onMeasure={reportRatio}
                    onToggleStatus={handleStatusToggle}
                    onDuplicate={setDuplicateTarget}
                    onDelete={setDeleteTarget}
                />
            )}
        />
    ) : (
        <div className="flex flex-col gap-2">
            {shown.map((tpl) => (
                <SortablePrintListRow
                    key={tpl.id}
                    tpl={tpl}
                    isBn={isBn}
                    reorderable={reorderable}
                    isOver={overId === tpl.id && activeId !== tpl.id}
                    dragEndedAt={dragEndedAt}
                    togglingId={togglingId}
                    duplicatingId={duplicatingId}
                    onToggleStatus={handleStatusToggle}
                    onDuplicate={setDuplicateTarget}
                    onDelete={setDeleteTarget}
                />
            ))}
        </div>
    );

    return (
        <div className="font-hind w-full py-4 md:py-6">
            {/* Header */}
            <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold tracking-[-0.01em] text-[#1C1B19] dark:text-zinc-100">
                        {headerTitle}
                    </h1>
                    <p className="mt-1 text-sm text-[#6E6A63] dark:text-zinc-400">
                        {t('printTemplatesAdmin.pageSubtitle')}
                    </p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                    {/* Quick sort: the admin's order / newest */}
                    <div className="hidden sm:flex items-center gap-0.5 p-0.5 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900">
                        <QuickSortPill active={sort === 'order'} icon={IconArrowsSort} label={t('printTemplatesAdmin.sortManual')} onClick={() => setSort('order')} />
                        <QuickSortPill active={sort === 'newest'} icon={IconClock} label={t('worldCupJersey.sortNewest')} onClick={() => setSort('newest')} />
                    </div>
                    {/* Quick Draft filter — toggles status between draft-only and all */}
                    <button
                        onClick={() => setStatusFilter(s => (s === 'draft' ? 'all' : 'draft'))}
                        title={t('printTemplatesAdmin.status_draft')}
                        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-[11px] border text-[13px] font-semibold transition-colors ${
                            statusFilter === 'draft'
                                ? 'border-amber-500 bg-amber-500 text-white'
                                : 'border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30'
                        }`}
                    >
                        <IconPencil size={15} className={statusFilter === 'draft' ? 'text-white' : 'text-amber-500'} />
                        <span className="hidden sm:inline">{t('printTemplatesAdmin.status_draft')}</span>
                    </button>
                    <Link
                        to="/admin/print-templates/new"
                        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[10px] bg-[#E2702E] hover:bg-[#E2702E]/90 text-white text-[13px] font-semibold transition-colors"
                    >
                        <IconPlus size={16} stroke={2} /> {t('printTemplatesAdmin.addNewTemplate')}
                    </Link>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-5 items-start">

                {/* ── Sidebar (desktop) ── */}
                <aside className="hidden lg:block lg:w-72 lg:flex-shrink-0 lg:sticky lg:top-4 self-start bg-white dark:bg-zinc-900 border border-[#ECE9E3] dark:border-zinc-700 rounded-2xl p-4">
                    {filterHeaderRow}
                    {filterBody}
                </aside>

                {/* ── Main ── */}
                <main ref={gridRef} className="flex-1 min-w-0 w-full">
                    {/* Search + mobile filter trigger */}
                    <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center gap-2.5 h-12 px-3.5 flex-1 min-w-0 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900">
                            <IconSearch size={18} className="text-[#9C988F] dark:text-zinc-400 flex-shrink-0" />
                            <input
                                type="text"
                                className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-[#1C1B19] dark:text-zinc-100 placeholder:text-[#9C988F] dark:placeholder:text-zinc-400"
                                placeholder={t('printTemplatesAdmin.searchPlaceholder')}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button type="button" onClick={() => setSearchQuery('')} className="flex-shrink-0 p-0.5 rounded-full text-[#9C988F] dark:text-zinc-400 hover:text-[#E2702E] hover:bg-[#F1EFEA] dark:hover:bg-zinc-800 transition-colors">
                                    <IconX size={16} />
                                </button>
                            )}
                        </div>
                        <button
                            onClick={() => setFilterSheetOpen(true)}
                            className="lg:hidden relative flex-shrink-0 inline-flex items-center gap-1.5 h-12 px-4 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm font-semibold text-[#1C1B19] dark:text-zinc-100 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors"
                        >
                            <IconAdjustmentsHorizontal size={18} className="text-[#E2702E]" />
                            <span className="hidden sm:inline">{t('worldCupJersey.filters')}</span>
                            {filtersDirty && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#E2702E]" />}
                        </button>
                    </div>

                    {/* Category marquee + result count + view toggle + sort */}
                    <div className="flex items-center gap-3 mb-4">
                        {categories.length > 0 ? (
                            <div
                                className="flex-1 min-w-0 relative overflow-hidden"
                                onMouseEnter={() => setMarqueePaused(true)}
                                onMouseLeave={() => setMarqueePaused(false)}
                                style={{ WebkitMaskImage: 'linear-gradient(to right, transparent, #000 3%, #000 92%, transparent)', maskImage: 'linear-gradient(to right, transparent, #000 3%, #000 92%, transparent)' }}
                            >
                                <div
                                    ref={marqueeRowRef}
                                    className="flex gap-2 w-max py-0.5"
                                    style={{
                                        animationName: 'admin-ptg-cat-marquee',
                                        animationDuration: `${marqueeDuration}s`,
                                        animationTimingFunction: 'linear',
                                        animationIterationCount: 'infinite',
                                        animationPlayState: marqueePaused ? 'paused' : 'running',
                                    }}
                                >
                                    {[...categories, ...categories].map((cat, i) => {
                                        const active = activeCategory === cat.slug;
                                        return (
                                            <button
                                                key={`${cat.slug}-${i}`}
                                                onClick={() => setActiveCategory(active ? 'all' : cat.slug)}
                                                className={`flex-shrink-0 text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${active
                                                    ? 'bg-[#E2702E] border-[#E2702E] text-white'
                                                    : 'text-[#6E6A63] dark:text-zinc-300 bg-white dark:bg-zinc-800 border-[#E9E6E0] dark:border-zinc-700 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 hover:text-[#E2702E]'}`}
                                            >
                                                {getCatName(cat)}
                                            </button>
                                        );
                                    })}
                                </div>
                                <style>{`@keyframes admin-ptg-cat-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
                            </div>
                        ) : <div className="flex-1" />}
                        <div className="flex-shrink-0 flex items-center gap-2.5">
                            <p className="text-[13px] text-[#6E6A63] dark:text-zinc-300 whitespace-nowrap hidden sm:block">
                                {savingOrder
                                    ? t('printTemplatesAdmin.savingOrder')
                                    : `${fmt(filtered.length)} ${t('worldCupJersey.templatesFound')}`}
                            </p>
                            {/* Grid / list view toggle */}
                            <div className="flex items-center gap-0.5 p-0.5 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900">
                                {[
                                    { key: 'grid', Icon: IconLayoutGrid, label: t('questionsCreate.viewGrid') },
                                    { key: 'list', Icon: IconList, label: t('questionsCreate.viewList') },
                                ].map(({ key, Icon, label }) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => changeView(key)}
                                        title={label}
                                        aria-label={label}
                                        aria-pressed={view === key}
                                        className={`flex h-8 w-8 items-center justify-center rounded-[9px] transition-colors cursor-pointer ${
                                            view === key
                                                ? 'bg-[#E2702E] text-white'
                                                : 'text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 hover:text-[#E2702E]'
                                        }`}
                                    >
                                        <Icon size={16} />
                                    </button>
                                ))}
                            </div>
                            <SortMenu sort={sort} setSort={setSort} dateRange={dateRange} setDateRange={setDateRange} />
                        </div>
                    </div>

                    {/* Reorder hint — says why the cards are or are not draggable */}
                    <p className="text-[12px] text-[#9C988F] dark:text-zinc-500 mb-3 flex items-center gap-1.5">
                        <IconGripVertical size={13} />
                        {reorderable ? t('printTemplatesAdmin.dragToReorder') : t('printTemplatesAdmin.reorderNeedsManualSort')}
                    </p>

                    {/* Skeleton */}
                    {loading && (view === 'grid' ? <SkeletonMasonry /> : <SkeletonList />)}

                    {/* Empty */}
                    {!loading && filtered.length === 0 && (
                        <div className="flex flex-col items-center gap-3 py-20">
                            <div className="w-16 h-16 rounded-2xl bg-[#F1EFEA] dark:bg-zinc-800 flex items-center justify-center">
                                <IconPrinter size={28} className="text-[#C3BEB4] dark:text-zinc-600" />
                            </div>
                            <p className="text-sm text-[#6E6A63] dark:text-zinc-400">
                                {filtersDirty ? t('printTemplatesAdmin.noMatchingTemplates') : t('printTemplatesAdmin.noTemplatesYet')}
                            </p>
                            {!filtersDirty && (
                                <Link to="/admin/print-templates/new" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[10px] bg-[#E2702E] hover:bg-[#E2702E]/90 text-white text-sm font-semibold transition-colors">
                                    <IconPlus size={16} /> {t('printTemplatesAdmin.createFirstTemplate')}
                                </Link>
                            )}
                        </div>
                    )}

                    {/* Grid / list */}
                    {!loading && filtered.length > 0 && (
                        <>
                            {reorderable ? (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragStart={({ active }) => { setActiveId(active.id); setOverId(active.id); }}
                                    onDragOver={({ over }) => setOverId(over?.id ?? null)}
                                    onDragEnd={handleDragEnd}
                                    onDragCancel={() => {
                                        dragEndedAt.current = Date.now();
                                        setActiveId(null);
                                        setOverId(null);
                                    }}
                                >
                                    <SortableContext items={shown.map((tpl) => tpl.id)} strategy={noShiftStrategy}>
                                        {grid}
                                    </SortableContext>
                                    <DragOverlay dropAnimation={null}>
                                        {activeTemplate ? (
                                            <div className="rotate-1 opacity-95 shadow-2xl rounded-2xl">
                                                {view === 'grid'
                                                    ? <PrintTemplateCard tpl={activeTemplate} isBn={isBn} />
                                                    : <PrintTemplateListRow tpl={activeTemplate} isBn={isBn} />}
                                            </div>
                                        ) : null}
                                    </DragOverlay>
                                </DndContext>
                            ) : grid}

                            {hasMore && (
                                <div ref={sentinelRef} className="flex justify-center items-center gap-2 mt-6 py-4 text-[#E2702E]">
                                    <IconLoader2 size={20} className="animate-spin" />
                                    <span className="text-sm font-medium">{t('common.loading')}</span>
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>

            {/* Filter drawer (mobile) */}
            <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
                <SheetContent side="left" className="w-[86%] max-w-xs overflow-y-auto p-4 font-hind">
                    <SheetTitle className="sr-only">{t('worldCupJersey.filters')}</SheetTitle>
                    <div className="pr-7">{filterHeaderRow}</div>
                    {filterBody}
                </SheetContent>
            </Sheet>

            <ConfirmDialog
                open={!!duplicateTarget}
                onClose={() => setDuplicateTarget(null)}
                onConfirm={handleDuplicate}
                loading={duplicatingId != null}
                variant="primary"
                title={t('printTemplatesAdmin.duplicateConfirmTitle')}
                description={t('printTemplatesAdmin.duplicateConfirmDesc', { name: duplicateTarget?.title })}
                confirmLabel={t('printTemplatesAdmin.duplicate')}
            />

            <ConfirmDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title={t('printTemplatesAdmin.deleteConfirmTitle')}
                description={t('printTemplatesAdmin.deleteConfirmDesc', { name: deleteTarget?.title })}
                confirmLabel={t('printTemplatesAdmin.delete')}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sidebar primitives
// ---------------------------------------------------------------------------
function FilterLabel({ children }) {
    return <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400 mb-2">{children}</p>;
}

function CategoryRowsSkeleton({ rows = 6 }) {
    return (
        <>
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg animate-pulse">
                    <span className="h-3 rounded bg-zinc-200 dark:bg-zinc-700" style={{ width: `${45 + ((i * 13) % 40)}%` }} />
                    <span className="ml-2 flex-shrink-0 h-4 w-7 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                </div>
            ))}
        </>
    );
}

function CategoryRow({ active, label, count, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center justify-between px-3 py-2 rounded-lg text-[13px] transition-colors ${
                active
                    ? 'font-semibold text-[#E2702E] bg-[#FCEFE7] dark:bg-orange-950/30'
                    : 'font-medium text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800'
            }`}
        >
            <span className="truncate text-left">{label}</span>
            <span className={`ml-2 flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                active ? 'text-[#E2702E] bg-white dark:bg-zinc-900' : 'text-[#A8A294] dark:text-zinc-400 bg-[#F1EFEA] dark:bg-zinc-800'
            }`}>{count}</span>
        </button>
    );
}

function RadioRow({ active, title, hint, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors ${
                active ? 'bg-[#FCEFE7] dark:bg-orange-950/30' : 'hover:bg-[#FAF9F6] dark:hover:bg-zinc-800'
            }`}
        >
            <span className={`w-4 h-4 rounded-full flex-shrink-0 border-[1.5px] transition-colors ${
                active ? 'border-[4.5px] border-[#E2702E]' : 'border-[#C3BEB4] dark:border-zinc-600'
            }`} />
            <span className="flex flex-col leading-tight">
                <span className="text-[13px] font-medium text-[#1C1B19] dark:text-zinc-100">{title}</span>
                {hint && <span className="text-[10.5px] text-[#9C988F] dark:text-zinc-400">{hint}</span>}
            </span>
        </button>
    );
}

function QuickSortPill({ active, icon: Icon, label, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-[9px] text-[13px] font-semibold transition-colors cursor-pointer ${
                active ? 'bg-[#E2702E] text-white' : 'text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30'
            }`}
        >
            <Icon size={15} className={active ? 'text-white' : 'text-[#E2702E]'} />
            <span className="hidden md:inline">{label}</span>
        </button>
    );
}

function SortRow({ active, icon: Icon, label, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] transition-colors ${
                active ? 'font-semibold text-[#E2702E] bg-[#FCEFE7] dark:bg-orange-950/30' : 'font-medium text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800'
            }`}
        >
            <Icon size={14} />
            {label}
        </button>
    );
}

// Top-right Sort / date-range dropdown.
function SortMenu({ sort, setSort, dateRange, setDateRange }) {
    const { t } = useTranslation();
    const sortOptions = [
        { value: 'order', label: t('printTemplatesAdmin.sortManual') },
        { value: 'popular', label: t('worldCupJersey.sortPopular') },
        { value: 'newest', label: t('worldCupJersey.sortNewest') },
        { value: 'oldest', label: t('worldCupJersey.sortOldest') },
        { value: 'name', label: t('worldCupJersey.sortName') },
    ];
    const dateOptions = [
        { value: 'all', label: t('worldCupJersey.dateAll') },
        { value: 'today', label: t('worldCupJersey.dateToday') },
        { value: '3days', label: t('worldCupJersey.date3Days') },
        { value: '7days', label: t('worldCupJersey.date7Days') },
        { value: '30days', label: t('worldCupJersey.date30Days') },
    ];
    const active = sort !== 'order' || dateRange !== 'all';

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button className="relative inline-flex items-center gap-1.5 h-9 px-3 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[13px] font-semibold text-[#1C1B19] dark:text-zinc-100 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors cursor-pointer">
                    <IconArrowsSort size={16} className="text-[#E2702E]" />
                    <span className="hidden sm:inline">{t('worldCupJersey.sortAction')}</span>
                    <IconChevronDown size={14} className="text-[#9C988F] dark:text-zinc-400" />
                    {active && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#E2702E]" />}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 font-hind">
                <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400">
                    <IconArrowsSort size={13} /> {t('worldCupJersey.sortBy')}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={sort} onValueChange={setSort}>
                    {sortOptions.map(o => (
                        <DropdownMenuRadioItem key={o.value} value={o.value} className="text-[13px] cursor-pointer">{o.label}</DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400">
                    <IconCalendar size={13} /> {t('worldCupJersey.dateRange')}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={dateRange} onValueChange={setDateRange}>
                    {dateOptions.map(o => (
                        <DropdownMenuRadioItem key={o.value} value={o.value} className="text-[13px] cursor-pointer">{o.label}</DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// ---------------------------------------------------------------------------
// Print template card — hover overlay + admin actions
// ---------------------------------------------------------------------------
function PrintTemplateCard({
    tpl, isBn, togglingId, duplicatingId, onMeasure, onToggleStatus, onDuplicate,
    onDelete, dragHandle, isOver, dragEndedAt,
}) {
    const { t } = useTranslation();
    const title = (isBn && tpl.titleBn) ? tpl.titleBn : tpl.title;
    const catName = (isBn && tpl.categoryRef?.nameBn) ? tpl.categoryRef.nameBn : tpl.categoryRef?.name;
    const [loaded, setLoaded] = useState(false);
    // Ratio (height/width) of the thumbnail image itself. The thumbnail can be
    // cropped to its own ratio, so once it loads the card follows the image
    // rather than the artboard size.
    const [thumbRatio, setThumbRatio] = useState(null);
    const toggling = togglingId === tpl.id;
    const duplicating = duplicatingId === tpl.id;
    const w = Number(tpl.width) || 1050;
    const h = Number(tpl.height) || 600;
    // Page size is only the pre-load estimate; the loaded thumbnail wins.
    const frameAspect = thumbRatio ? `1 / ${thumbRatio}` : `${w} / ${h}`;
    const { ref, isDragging, ...handleProps } = dragHandle || {};

    return (
        // A real link (not onClick+navigate) so the browser offers open-in-new-tab,
        // middle-click and cmd/ctrl+click. Nested action buttons preventDefault so
        // they don't trigger the link navigation.
        <Link
            to={`/admin/print-templates/${tpl.id}/edit`}
            ref={ref}
            {...handleProps}
            onClick={(e) => {
                // A drag that ends on this card still fires a click on release.
                if (dragEndedAt && Date.now() - dragEndedAt.current < 250) e.preventDefault();
            }}
            className={`group block mb-4 rounded-2xl overflow-hidden border bg-white dark:bg-zinc-900 relative transition-all duration-150 hover:shadow-lg hover:-translate-y-0.5 ${
                dragHandle ? 'touch-none cursor-grab' : 'cursor-pointer'
            } ${
                isDragging
                    ? 'opacity-35 border-[#ECE9E3] dark:border-zinc-700'
                    : isOver
                        ? 'border-[#E2702E] ring-2 ring-[#E2702E]/60'
                        : 'border-[#ECE9E3] dark:border-zinc-700 hover:border-[#E2702E]/40'
            }`}
        >
            <div className="relative">
                {tpl.thumbnail ? (
                    // Reserve the thumbnail's height up-front from the template's own
                    // page size, so the card never collapses then reflows on load.
                    <div
                        className="relative w-full overflow-hidden bg-[#F0EFEC] dark:bg-zinc-800"
                        style={{ aspectRatio: frameAspect }}
                    >
                        {!loaded && <div className="absolute inset-0 animate-pulse bg-[#E9E7E2] dark:bg-zinc-700/60" />}
                        <img
                            src={tpl.thumbnail}
                            alt={title}
                            loading="lazy"
                            draggable={false}
                            className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                            onLoad={(e) => {
                                const { naturalWidth, naturalHeight } = e.currentTarget;
                                if (naturalWidth) {
                                    const r = naturalHeight / naturalWidth;
                                    setThumbRatio(r);
                                    onMeasure?.(tpl.id, r);
                                }
                                setLoaded(true);
                            }}
                            onError={() => setLoaded(true)}
                        />
                    </div>
                ) : (
                    <div
                        className="w-full flex items-center justify-center bg-[#F1EFEA] dark:bg-zinc-800"
                        style={{ aspectRatio: frameAspect }}
                    >
                        <IconPrinter size={28} className="text-[#C3BEB4] dark:text-zinc-600" />
                    </div>
                )}

                {/* Top-left: status pill (click toggles active/draft) + Free badge */}
                <div className="absolute top-2.5 left-2.5 z-10 flex items-center gap-1.5">
                    <button
                        title={tpl.status === 'archived' ? t('printTemplatesAdmin.status_archived') : (tpl.status === 'active' ? t('printTemplatesAdmin.setDraft') : t('printTemplatesAdmin.setActive'))}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (tpl.status !== 'archived') onToggleStatus?.(tpl); }}
                        className={`inline-flex items-center px-2.5 py-[3px] rounded-full text-[10.5px] font-semibold backdrop-blur-sm ${STATUS_PILL[tpl.status] || STATUS_PILL.archived} ${toggling ? 'cursor-wait' : tpl.status !== 'archived' ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                        {toggling ? '…' : t(`printTemplatesAdmin.status_${tpl.status}`)}
                    </button>
                    {tpl.isFree && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-flex items-center text-[10.5px] font-bold text-white bg-emerald-500 px-2.5 py-[3px] rounded-full shadow-sm cursor-help">
                                    {t('profile.creditFree')}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>{t('profile.freeTemplateTooltip')}</TooltipContent>
                        </Tooltip>
                    )}
                </div>

                {/* Top-right: how many times it has been exported, hidden on
                    hover so the action cluster can take the slot. */}
                {tpl.usageCount > 0 && (
                    <div className="absolute top-2.5 right-2.5 z-10 group-hover:opacity-0 transition-opacity">
                        <span className="inline-flex items-center px-2.5 py-[3px] rounded-full text-[10.5px] font-semibold text-white bg-black/55 backdrop-blur-sm shadow-sm whitespace-nowrap">
                            {formatUses(tpl.usageCount)} {t('worldCupJersey.uses')}
                        </span>
                    </div>
                )}

                {/* Hover action cluster (top-right): edit + duplicate + delete */}
                <div className="absolute top-2.5 right-2.5 z-20 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            {/* No onClick — the surrounding card link handles the navigation,
                                so new-tab shortcuts work on this icon too. */}
                            <span className="w-7 h-7 rounded-lg bg-white/95 dark:bg-zinc-800/95 border border-[#ECE9E3] dark:border-zinc-700 flex items-center justify-center text-[#6E6A63] dark:text-zinc-400 hover:text-[#E2702E] shadow-sm cursor-pointer">
                                <IconEdit size={14} />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{t('printTemplatesAdmin.editTemplate')}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button disabled={duplicating} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDuplicate?.(tpl); }}
                                className="w-7 h-7 rounded-lg bg-white/95 dark:bg-zinc-800/95 border border-[#ECE9E3] dark:border-zinc-700 flex items-center justify-center text-[#6E6A63] dark:text-zinc-400 hover:text-[#E2702E] shadow-sm cursor-pointer disabled:opacity-50 disabled:cursor-wait">
                                {duplicating ? <span className="w-3 h-3 border border-[#E2702E] border-t-transparent rounded-full animate-spin inline-block" /> : <IconCopy size={14} />}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{t('printTemplatesAdmin.duplicate')}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete?.(tpl); }}
                                className="w-7 h-7 rounded-lg bg-white/95 dark:bg-zinc-800/95 border border-[#ECE9E3] dark:border-zinc-700 flex items-center justify-center text-red-500 hover:text-red-600 shadow-sm cursor-pointer">
                                <IconTrash size={14} />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{t('printTemplatesAdmin.delete')}</TooltipContent>
                    </Tooltip>
                </div>

                {/* Hover overlay: name + category/slug pinned to bottom (whole card click = edit) */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/75 via-black/10 to-black/25 cursor-pointer">
                    <div className="absolute inset-x-0 bottom-0 px-3.5 py-2.5">
                        {/* Who made it — hover-only, like the AI card's creator chip */}
                        {tpl.createdByName && (
                            <span
                                title={t('printTemplatesAdmin.createdByTooltip')}
                                className="inline-flex items-center gap-1 mb-1.5 max-w-full px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/20 text-white backdrop-blur-sm"
                            >
                                <IconUser size={11} className="flex-shrink-0" />
                                <span className="truncate">{tpl.createdByName}</span>
                            </span>
                        )}
                        <p className="text-[13px] font-semibold text-white truncate">{title}</p>
                        {/* Category then slug on their own lines — the slug is the
                            identifier you copy, so it gets the mono treatment and the
                            quietest colour of the three. */}
                        <p className="text-[11px] text-white/70 mt-0.5 truncate">
                            {catName || t('printTemplatesAdmin.uncategorized')}
                        </p>
                        <p className="text-[10px] text-white/45 mt-0.5 truncate font-mono">{tpl.slug}</p>
                    </div>
                </div>
            </div>
        </Link>
    );
}

/** Sortable wrapper — kept apart so DragOverlay can render a plain card. */
function SortablePrintCard({ reorderable, ...props }) {
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
        id: props.tpl.id,
        disabled: !reorderable,
    });
    if (!reorderable) return <PrintTemplateCard {...props} />;
    return (
        <PrintTemplateCard
            {...props}
            dragHandle={{ ref: setNodeRef, isDragging, ...attributes, ...listeners }}
        />
    );
}

// ---------------------------------------------------------------------------
// Print template list row — compact scannable variant of the card
// ---------------------------------------------------------------------------
function PrintTemplateListRow({
    tpl, isBn, togglingId, duplicatingId, onToggleStatus, onDuplicate, onDelete,
    dragHandle, isOver, dragEndedAt,
}) {
    const { t, i18n } = useTranslation();
    const title = (isBn && tpl.titleBn) ? tpl.titleBn : tpl.title;
    const catName = (isBn && tpl.categoryRef?.nameBn) ? tpl.categoryRef.nameBn : tpl.categoryRef?.name;
    const toggling = togglingId === tpl.id;
    const duplicating = duplicatingId === tpl.id;
    const w = Number(tpl.width) || 1050;
    const h = Number(tpl.height) || 600;
    const { ref, isDragging, ...handleProps } = dragHandle || {};
    const created = tpl.createdAt
        ? new Date(tpl.createdAt).toLocaleDateString(i18n.language === 'bn' ? 'bn-BD' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })
        : null;

    return (
        <Link
            to={`/admin/print-templates/${tpl.id}/edit`}
            ref={ref}
            {...handleProps}
            onClick={(e) => {
                if (dragEndedAt && Date.now() - dragEndedAt.current < 250) e.preventDefault();
            }}
            className={`group flex items-center gap-3 px-3 py-2 rounded-xl border bg-white dark:bg-zinc-900 transition-all hover:shadow-sm ${
                dragHandle ? 'touch-none cursor-grab' : ''
            } ${
                isDragging
                    ? 'opacity-35 border-[#ECE9E3] dark:border-zinc-700'
                    : isOver
                        ? 'border-[#E2702E] ring-2 ring-[#E2702E]/60'
                        : 'border-[#ECE9E3] dark:border-zinc-700 hover:border-[#E2702E]/40'
            }`}
        >
            {tpl.thumbnail ? (
                <img
                    src={tpl.thumbnail}
                    alt={title}
                    loading="lazy"
                    draggable={false}
                    className="w-12 h-14 flex-shrink-0 rounded-lg object-contain bg-[#F0EFEC] dark:bg-zinc-800"
                />
            ) : (
                <div className="w-12 h-14 flex-shrink-0 rounded-lg flex items-center justify-center bg-[#F1EFEA] dark:bg-zinc-800">
                    <IconPrinter size={18} className="text-[#C3BEB4] dark:text-zinc-600" />
                </div>
            )}

            <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 min-w-0">
                    <p className="text-[13.5px] font-semibold text-[#1C1B19] dark:text-zinc-100 truncate">{title}</p>
                    <span className="flex-shrink-0 text-[10.5px] font-mono text-[#C3BEB4] dark:text-zinc-600 truncate max-w-[180px]">{tpl.slug}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
                    <span className="flex-shrink-0 text-[11.5px] text-[#9C988F] dark:text-zinc-400 truncate max-w-[160px]">
                        {catName || t('printTemplatesAdmin.uncategorized')}
                    </span>
                    <span className="flex-shrink-0 text-[#C3BEB4] dark:text-zinc-600">·</span>
                    <span className="flex-shrink-0 text-[11.5px] text-[#9C988F] dark:text-zinc-400">{w}×{h}</span>
                    {tpl.createdByName && (
                        <span title={t('printTemplatesAdmin.createdByTooltip')} className="inline-flex items-center gap-1 min-w-0 text-[11.5px] text-[#9C988F] dark:text-zinc-400">
                            <span className="flex-shrink-0 text-[#C3BEB4] dark:text-zinc-600">·</span>
                            <IconUser size={11} className="flex-shrink-0" />
                            <span className="truncate max-w-[140px]">{tpl.createdByName}</span>
                        </span>
                    )}
                    {tpl.isFree && (
                        <span className="inline-flex flex-shrink-0 items-center px-1.5 py-px rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                            {t('profile.creditFree')}
                        </span>
                    )}
                </div>
            </div>

            <span className="hidden sm:block flex-shrink-0 w-16 text-right text-[12px] text-[#6E6A63] dark:text-zinc-400 whitespace-nowrap">
                {formatUses(tpl.usageCount || 0)} {t('worldCupJersey.uses')}
            </span>

            {created && (
                <span className="hidden md:block flex-shrink-0 w-24 text-right text-[12px] text-[#9C988F] dark:text-zinc-400 whitespace-nowrap">
                    {created}
                </span>
            )}

            <button
                title={tpl.status === 'archived' ? t('printTemplatesAdmin.status_archived') : (tpl.status === 'active' ? t('printTemplatesAdmin.setDraft') : t('printTemplatesAdmin.setActive'))}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (tpl.status !== 'archived') onToggleStatus?.(tpl); }}
                className={`flex-shrink-0 inline-flex items-center justify-center w-[74px] px-2 py-[3px] rounded-full text-[10.5px] font-semibold ${STATUS_PILL[tpl.status] || STATUS_PILL.archived} ${toggling ? 'cursor-wait' : tpl.status !== 'archived' ? 'cursor-pointer' : 'cursor-default'}`}
            >
                {toggling ? '…' : t(`printTemplatesAdmin.status_${tpl.status}`)}
            </button>

            <div className="flex-shrink-0 flex items-center gap-1">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="w-7 h-7 rounded-lg border border-[#ECE9E3] dark:border-zinc-700 flex items-center justify-center text-[#6E6A63] dark:text-zinc-400 group-hover:text-[#E2702E] transition-colors cursor-pointer">
                            <IconEdit size={14} />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">{t('printTemplatesAdmin.editTemplate')}</TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button disabled={duplicating} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDuplicate?.(tpl); }}
                            className="w-7 h-7 rounded-lg border border-[#ECE9E3] dark:border-zinc-700 flex items-center justify-center text-[#6E6A63] dark:text-zinc-400 hover:text-[#E2702E] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-wait">
                            {duplicating ? <span className="w-3 h-3 border border-[#E2702E] border-t-transparent rounded-full animate-spin inline-block" /> : <IconCopy size={14} />}
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">{t('printTemplatesAdmin.duplicate')}</TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete?.(tpl); }}
                            className="w-7 h-7 rounded-lg border border-[#ECE9E3] dark:border-zinc-700 flex items-center justify-center text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer">
                            <IconTrash size={14} />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">{t('printTemplatesAdmin.delete')}</TooltipContent>
                </Tooltip>
            </div>
        </Link>
    );
}

function SortablePrintListRow({ reorderable, ...props }) {
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
        id: props.tpl.id,
        disabled: !reorderable,
    });
    if (!reorderable) return <PrintTemplateListRow {...props} />;
    return (
        <PrintTemplateListRow
            {...props}
            dragHandle={{ ref: setNodeRef, isDragging, ...attributes, ...listeners }}
        />
    );
}

// ---------------------------------------------------------------------------
// Skeletons — staggered heights to mimic the real grid
// ---------------------------------------------------------------------------
function SkeletonList({ rows = 8 }) {
    return (
        <div className="flex flex-col gap-2">
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className="h-[72px] rounded-xl border border-[#ECE9E3] dark:border-zinc-700 bg-[#F0EFEC] dark:bg-zinc-800 animate-pulse" />
            ))}
        </div>
    );
}

function SkeletonMasonry() {
    const heights = ['h-[260px]', 'h-[200px]', 'h-[300px]', 'h-[230px]', 'h-[280px]', 'h-[210px]', 'h-[320px]', 'h-[240px]', 'h-[260px]'];
    return (
        <Masonry breakpointCols={masonryBreakpoints} className="flex w-auto -ml-4" columnClassName="pl-4 bg-clip-padding">
            {heights.map((h, i) => (
                <div key={i} className={`mb-4 rounded-2xl overflow-hidden border border-[#ECE9E3] dark:border-zinc-700 bg-[#F0EFEC] dark:bg-zinc-800 animate-pulse ${h}`} />
            ))}
        </Masonry>
    );
}
